import { ReactNode } from "react";
import { Link, useLocation } from "react-router-dom";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { useAuth } from "@/contexts/AuthContext";
import { Loader2, ShieldAlert } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Client-side admin gate. The real security boundary is RLS on the database;
 * this just hides UI from non-admins and gives them a friendly message.
 */
const RequireAdmin = ({ children }: { children: ReactNode }) => {
  const { user, loading: authLoading } = useAuth();
  const { isAdmin, loading } = useIsAdmin();
  const location = useLocation();

  if (authLoading || loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-md space-y-4 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
          <h1 className="font-serif text-2xl text-foreground">Sign in required</h1>
          <p className="text-sm text-muted-foreground">
            This page is restricted to administrators. Please sign in to continue.
          </p>
          <Button asChild>
            <Link to={`/auth?redirect=${encodeURIComponent(location.pathname)}`}>
              Sign in
            </Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-background px-6">
        <div className="max-w-md space-y-4 text-center">
          <ShieldAlert className="mx-auto h-10 w-10 text-muted-foreground" />
          <h1 className="font-serif text-2xl text-foreground">Admins only</h1>
          <p className="text-sm text-muted-foreground">
            Your account doesn't have access to this page. If you believe this
            is a mistake, ask an administrator to grant you the{" "}
            <code className="font-mono text-xs">admin</code> role.
          </p>
          <Button variant="outline" asChild>
            <Link to="/">Return home</Link>
          </Button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};

export default RequireAdmin;
